/**
 * <templ-product-tabs>
 * Accessible tablist that swaps between panels, each holding a Swiper
 * carousel. Swiper is only instantiated for a panel the first time it
 * becomes visible, so hidden tabs never pay for layout/measurement work.
 */
(function () {
  if (customElements.get('templ-product-tabs')) return;

  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const EDGE_SWIPE_THRESHOLD = 50;

  const SWIPER_OPTIONS = {
    slidesPerView: 1.25,
    spaceBetween: 14,
    speed: REDUCED_MOTION ? 0 : 450,
    grabCursor: true,
    watchOverflow: true,
    a11y: { enabled: true },
    keyboard: { enabled: true, onlyInViewport: true },
    pagination: { clickable: true, el: null },
    breakpoints: {
      480: { slidesPerView: 1.4, spaceBetween: 16 },
      750: { slidesPerView: 2.4, spaceBetween: 20 },
      1000: { slidesPerView: 3, spaceBetween: 24 },
      1200: { slidesPerView: 4, spaceBetween: 24 },
    },
  };

  class TemplProductTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      this.swipers = new Map();

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.activate(index));
        tab.addEventListener('keydown', (event) => this.onTabKeydown(event, index));
      });

      const activeIndex = Math.max(
        this.tabs.findIndex((tab) => tab.classList.contains('is-active')),
        0
      );
      this.initPanel(activeIndex);
    }

    onTabKeydown(event, index) {
      const { key } = event;
      let nextIndex = null;

      if (key === 'ArrowRight') nextIndex = (index + 1) % this.tabs.length;
      else if (key === 'ArrowLeft') nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
      else if (key === 'Home') nextIndex = 0;
      else if (key === 'End') nextIndex = this.tabs.length - 1;
      else return;

      event.preventDefault();
      this.activate(nextIndex);
      this.tabs[nextIndex].focus();
    }

    activate(index) {
      this.tabs.forEach((tab, i) => {
        const isActive = i === index;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', String(isActive));
        tab.tabIndex = isActive ? 0 : -1;
      });

      this.panels.forEach((panel, i) => {
        const isActive = i === index;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });

      this.initPanel(index);
    }

    // Switching tabs from a swipe is a touch gesture, not a keyboard action,
    // so unlike onTabKeydown this deliberately does not move focus.
    activateAdjacentTab(currentIndex, direction) {
      const nextIndex = currentIndex + direction;
      if (nextIndex < 0 || nextIndex >= this.tabs.length) return;
      this.activate(nextIndex);
      this.tabs[nextIndex].scrollIntoView({ inline: 'nearest', block: 'nearest' });
    }

    // Continuing to swipe past a carousel's last/first card jumps to the
    // next/previous tab, so the user never has to reach up and tap a tab.
    setupEdgeSwipe(el, swiper, index) {
      let startX = null;
      let wasAtEnd = false;
      let wasAtBeginning = false;

      const onPointerDown = (event) => {
        startX = event.clientX;
        wasAtEnd = swiper.isEnd;
        wasAtBeginning = swiper.isBeginning;
      };

      const onPointerUp = (event) => {
        if (startX === null) return;
        const deltaX = startX - event.clientX;
        startX = null;

        if (wasAtEnd && deltaX > EDGE_SWIPE_THRESHOLD) {
          this.activateAdjacentTab(index, 1);
        } else if (wasAtBeginning && deltaX < -EDGE_SWIPE_THRESHOLD) {
          this.activateAdjacentTab(index, -1);
        }
      };

      const onPointerCancel = () => {
        startX = null;
      };

      el.addEventListener('pointerdown', onPointerDown, { passive: true });
      el.addEventListener('pointerup', onPointerUp, { passive: true });
      el.addEventListener('pointercancel', onPointerCancel, { passive: true });
    }

    initPanel(index) {
      const panel = this.panels[index];
      if (!panel) return;

      const existing = this.swipers.get(index);
      if (existing) {
        requestAnimationFrame(() => existing.update());
        return;
      }

      requestAnimationFrame(() => {
        const el = panel.querySelector('.templ-swiper');
        if (!el || typeof Swiper === 'undefined') return;

        const options = Object.assign({}, SWIPER_OPTIONS, {
          pagination: Object.assign({}, SWIPER_OPTIONS.pagination, {
            el: panel.querySelector('.swiper-pagination'),
          }),
        });

        const swiper = new Swiper(el, options);
        const prevButton = panel.querySelector('.templ-swiper__arrow--prev');
        const nextButton = panel.querySelector('.templ-swiper__arrow--next');

        const syncArrows = () => {
          if (prevButton) prevButton.disabled = swiper.isBeginning;
          if (nextButton) nextButton.disabled = swiper.isEnd;
        };

        const syncLock = () => {
          el.classList.toggle('is-underfilled', swiper.isLocked);
        };

        if (prevButton) prevButton.addEventListener('click', () => swiper.slidePrev());
        if (nextButton) nextButton.addEventListener('click', () => swiper.slideNext());
        swiper.on('slideChange', syncArrows);
        swiper.on('reachBeginning', syncArrows);
        swiper.on('reachEnd', syncArrows);
        swiper.on('lock', syncLock);
        swiper.on('unlock', syncLock);
        swiper.on('resize', syncLock);
        syncArrows();
        syncLock();

        this.setupEdgeSwipe(el, swiper, index);

        this.swipers.set(index, swiper);
      });
    }
  }

  customElements.define('templ-product-tabs', TemplProductTabs);
})();
